/* ============================================================
   SUSHENAH Animation System
   GSAP + ScrollTrigger for Awwwards-quality animations
   ============================================================ */

// Hero elements selector (used in multiple places)
// Includes .word spans for headline word-by-word animation fallback
const HERO_SELECTORS = '.reveal-text, .reveal-element, .cause-card, .trust-badge, .product-card, .hero__headline, .hero__headline .word, .hero__subheadline, .hero__eyebrow, .hero__actions, .hero__cta, .hero__secondary-cta';

// Make elements visible - used for fallback and bfcache restore
const makeHeroVisible = () => {
  document.querySelectorAll(HERO_SELECTORS).forEach(el => {
    el.style.opacity = '1';
    el.style.transform = 'none';
  });
};

// Handle back-forward cache (bfcache) - CSS animations don't replay on restore
window.addEventListener('pageshow', function(event) {
  if (event.persisted) {
    // Page was restored from bfcache - make elements visible immediately
    console.log('Page restored from bfcache - ensuring hero visibility');
    makeHeroVisible();
    // Reset ScrollTrigger state to ensure hero fade is at correct position
    if (typeof ScrollTrigger !== 'undefined') {
      ScrollTrigger.refresh();
    }
  }
});

document.addEventListener('DOMContentLoaded', function() {
  // Fallback: Make elements visible immediately if GSAP is not loaded
  // This prevents invisible content
  const makeVisible = () => {
    document.querySelectorAll(HERO_SELECTORS).forEach(el => {
      el.style.opacity = '1';
      el.style.transform = 'none';
    });
  };

  // Wait for GSAP to load
  if (typeof gsap === 'undefined') {
    console.warn('GSAP not loaded. Animations disabled, making elements visible.');
    // Remove gsap-ready class (added in <head>) so CSS shows content
    document.documentElement.classList.remove('gsap-ready');
    makeVisible();
    return;
  }

  // gsap-ready class already added in <head> via inline script for instant hiding
  // No need to add it here - this prevents flash of visible content

  // Register GSAP plugins
  if (typeof ScrollTrigger !== 'undefined') {
    gsap.registerPlugin(ScrollTrigger);
  }
  if (typeof ScrollToPlugin !== 'undefined') {
    gsap.registerPlugin(ScrollToPlugin);
  }

  // Check for reduced motion preference
  const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  if (prefersReducedMotion) {
    // Make elements visible without animation
    document.querySelectorAll('.reveal-text, .reveal-element, .cause-card, .trust-badge, .product-card').forEach(el => {
      el.style.opacity = '1';
      el.style.transform = 'none';
    });
    return;
  }

  // ============================================================
  // 1. TEXT REVEAL ANIMATIONS
  // ============================================================

  // Heading reveal animation
  gsap.utils.toArray('.reveal-text').forEach((element) => {
    gsap.from(element, {
      y: 60,
      opacity: 0,
      duration: 0.8,
      ease: 'power3.out',
      scrollTrigger: {
        trigger: element,
        start: 'top 85%',
        once: true
      }
    });
  });

  // Split text animation for hero headline
  const heroHeadline = document.querySelector('.hero__headline');
  if (heroHeadline) {
    // Simple word-by-word reveal
    const text = heroHeadline.textContent;
    const words = text.split(' ');
    heroHeadline.innerHTML = words.map(word => `<span class="word">${word}</span>`).join(' ');

    // Use fromTo() to explicitly set end state (CSS has opacity:0)
    gsap.fromTo(heroHeadline.querySelectorAll('.word'),
      { y: 40, opacity: 0 },
      { y: 0, opacity: 1, duration: 0.6, stagger: 0.08, ease: 'power2.out', delay: 0.3 }
    );
  }

  // Animate other hero elements (eyebrow, subheadline, actions)
  const heroEyebrow = document.querySelector('.hero__eyebrow');
  if (heroEyebrow) {
    gsap.fromTo(heroEyebrow,
      { y: 20, opacity: 0 },
      { y: 0, opacity: 1, duration: 0.5, ease: 'power2.out', delay: 0.1 }
    );
  }

  const heroSubheadline = document.querySelector('.hero__subheadline');
  if (heroSubheadline) {
    gsap.fromTo(heroSubheadline,
      { y: 30, opacity: 0 },
      { y: 0, opacity: 1, duration: 0.6, ease: 'power2.out', delay: 0.5 }
    );
  }

  const heroActions = document.querySelector('.hero__actions');
  if (heroActions) {
    gsap.fromTo(heroActions,
      { y: 30, opacity: 0 },
      { y: 0, opacity: 1, duration: 0.6, ease: 'power2.out', delay: 0.7 }
    );
  }

  // ============================================================
  // 2. CARD STAGGER ANIMATIONS
  // ============================================================

  // Cause cards stagger reveal
  const causeCards = document.querySelectorAll('.cause-card');
  if (causeCards.length) {
    gsap.from(causeCards, {
      y: 40,
      opacity: 0,
      duration: 0.6,
      stagger: 0.12,
      ease: 'power2.out',
      scrollTrigger: {
        trigger: '.cause-navigator',
        start: 'top 70%',
        once: true
      }
    });
  }

  // Product cards stagger reveal
  const productCards = document.querySelectorAll('.product-card');
  if (productCards.length) {
    gsap.from(productCards, {
      y: 30,
      opacity: 0,
      duration: 0.5,
      stagger: 0.1,
      ease: 'power2.out',
      scrollTrigger: {
        trigger: '.featured-products',
        start: 'top 75%',
        once: true
      }
    });
  }

  // Testimonial cards
  const testimonialCards = document.querySelectorAll('.testimonial-card');
  if (testimonialCards.length) {
    gsap.from(testimonialCards, {
      y: 30,
      opacity: 0,
      duration: 0.5,
      stagger: 0.15,
      ease: 'power2.out',
      scrollTrigger: {
        trigger: '.testimonials',
        start: 'top 75%',
        once: true
      }
    });
  }

  // ============================================================
  // 3. TRUST BADGE ENTRANCE
  // ============================================================

  const trustBadges = document.querySelectorAll('.trust-badge');
  if (trustBadges.length) {
    gsap.from(trustBadges, {
      y: 30,
      opacity: 0,
      duration: 0.5,
      stagger: 0.1,
      ease: 'back.out(1.2)',
      scrollTrigger: {
        trigger: '.trust-strip',
        start: 'top 85%',
        once: true
      }
    });
  }

  // ============================================================
  // 4. HERO SECTION ANIMATIONS
  // ============================================================

  // Hero parallax effect
  const heroSection = document.querySelector('.hero');
  const heroBackground = document.querySelector('.hero__background');

  if (heroSection && heroBackground) {
    gsap.to(heroBackground, {
      yPercent: 20,
      ease: 'none',
      scrollTrigger: {
        trigger: heroSection,
        start: 'top top',
        end: 'bottom top',
        scrub: 1
      }
    });
  }

  // Hero content fade out on scroll
  const heroContent = document.querySelector('.hero__content');
  if (heroContent) {
    gsap.to(heroContent, {
      opacity: 0,
      y: -50,
      ease: 'none',
      scrollTrigger: {
        trigger: heroSection,
        start: 'center center',
        end: 'bottom top',
        scrub: 1,
        invalidateOnRefresh: true  // Prevent stale state on bfcache restore
      }
    });
  }

  // ============================================================
  // 5. FLOATING BLOB ANIMATIONS (CSS Enhanced)
  // ============================================================

  const blobs = document.querySelectorAll('.hero-blob');
  blobs.forEach((blob, index) => {
    // Add subtle mouse-following effect
    document.addEventListener('mousemove', (e) => {
      const mouseX = e.clientX / window.innerWidth - 0.5;
      const mouseY = e.clientY / window.innerHeight - 0.5;

      gsap.to(blob, {
        x: mouseX * (20 + index * 10),
        y: mouseY * (20 + index * 10),
        duration: 1.5,
        ease: 'power2.out'
      });
    });
  });

  // ============================================================
  // 6. SECTION REVEAL ON SCROLL
  // ============================================================

  gsap.utils.toArray('.section').forEach((section) => {
    const elements = section.querySelectorAll('.reveal-element');

    if (elements.length) {
      gsap.from(elements, {
        y: 30,
        opacity: 0,
        duration: 0.6,
        stagger: 0.1,
        ease: 'power2.out',
        scrollTrigger: {
          trigger: section,
          start: 'top 80%',
          once: true
        }
      });
    }
  });

  // ============================================================
  // 7. BUTTON HOVER ANIMATIONS
  // ============================================================

  // CTA button subtle pulse
  const ctaButtons = document.querySelectorAll('.btn-cta');
  ctaButtons.forEach((btn) => {
    // Gentle attention-grabbing pulse after 2 seconds on screen
    gsap.fromTo(btn,
      { boxShadow: '0 0 0 0 rgba(198, 93, 0, 0.4)' },
      {
        boxShadow: '0 0 0 8px rgba(198, 93, 0, 0)',
        duration: 1.5,
        repeat: 2,
        ease: 'power2.out',
        delay: 2,
        scrollTrigger: {
          trigger: btn,
          start: 'top 90%',
          once: true
        }
      }
    );
  });

  // ============================================================
  // 8. HEADER SCROLL BEHAVIOR
  // ============================================================

  const header = document.querySelector('.header');
  if (header) {
    let lastScrollTop = 0;

    ScrollTrigger.create({
      start: 'top -80',
      onUpdate: (self) => {
        const scrollTop = self.scroll();

        // Add shadow on scroll
        if (scrollTop > 80) {
          header.classList.add('header--scrolled');
        } else {
          header.classList.remove('header--scrolled');
        }

        // Hide/show on scroll direction (optional - uncomment if desired)
        // if (scrollTop > lastScrollTop && scrollTop > 200) {
        //   header.classList.add('header--hidden');
        // } else {
        //   header.classList.remove('header--hidden');
        // }

        lastScrollTop = scrollTop;
      }
    });
  }

  // ============================================================
  // 9. COUNTER ANIMATIONS
  // ============================================================

  const counters = document.querySelectorAll('[data-counter]');
  counters.forEach((counter) => {
    const target = parseInt(counter.dataset.counter, 10);

    ScrollTrigger.create({
      trigger: counter,
      start: 'top 85%',
      once: true,
      onEnter: () => {
        gsap.to(counter, {
          textContent: target,
          duration: 2,
          ease: 'power2.out',
          snap: { textContent: 1 },
          onUpdate: function() {
            counter.textContent = Math.floor(this.targets()[0].textContent);
          }
        });
      }
    });
  });

  // ============================================================
  // 10. SMOOTH ANCHOR SCROLLING
  // ============================================================

  document.querySelectorAll('a[href^="#"]').forEach(anchor => {
    anchor.addEventListener('click', function(e) {
      const targetId = this.getAttribute('href');
      if (targetId === '#') return;

      const targetElement = document.querySelector(targetId);
      if (targetElement) {
        e.preventDefault();
        gsap.to(window, {
          scrollTo: {
            y: targetElement,
            offsetY: 80 // Account for sticky header
          },
          duration: 1,
          ease: 'power2.inOut'
        });
      }
    });
  });

  // ============================================================
  // 11. NEWSLETTER FORM ANIMATIONS
  // ============================================================

  const newsletterForm = document.querySelector('.newsletter-form');
  if (newsletterForm) {
    const input = newsletterForm.querySelector('input');
    const button = newsletterForm.querySelector('button');

    if (input) {
      input.addEventListener('focus', () => {
        gsap.to(newsletterForm, {
          scale: 1.02,
          duration: 0.3,
          ease: 'power2.out'
        });
      });

      input.addEventListener('blur', () => {
        gsap.to(newsletterForm, {
          scale: 1,
          duration: 0.3,
          ease: 'power2.out'
        });
      });
    }
  }

  // ============================================================
  // REFRESH SCROLL TRIGGERS ON SHOPIFY DESIGN MODE
  // ============================================================

  if (Shopify.designMode) {
    document.addEventListener('shopify:section:load', () => {
      ScrollTrigger.refresh();
    });

    document.addEventListener('shopify:section:unload', () => {
      ScrollTrigger.refresh();
    });
  }

  console.log('SUSHENAH Animations initialized');

  // Safety timeout: Ensure all animated elements become visible after 1.5 seconds
  // This catches any edge cases where animations don't trigger properly
  // Reduced from 3s since content is hidden from start (no flash concern)
  setTimeout(() => {
    document.querySelectorAll(HERO_SELECTORS).forEach(el => {
      if (window.getComputedStyle(el).opacity === '0') {
        el.style.opacity = '1';
        el.style.transform = 'none';
        console.log('Safety fallback: Made element visible', el);
      }
    });
  }, 1500);
});

// Additional immediate check on page load (not inside DOMContentLoaded)
// This ensures hero is visible even if DOMContentLoaded fires late
window.addEventListener('load', function() {
  // After all resources load, check if hero is still invisible
  setTimeout(() => {
    document.querySelectorAll(HERO_SELECTORS).forEach(el => {
      if (window.getComputedStyle(el).opacity === '0') {
        el.style.opacity = '1';
        el.style.transform = 'none';
        console.log('Load fallback: Made element visible', el);
      }
    });
  }, 500);
});
